import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forTemplate } from '@trello/i18n';
import { Popover, PopoverContent, usePopover } from '@trello/nachos/popover';
import { useWorkspaceSettingsOrganizationUpdateDomainMutation } from './WorkspaceSettingsOrganizationUpdateDomainMutation.generated';
import type { WorkspaceSettingsOrganizationMemberQuery } from './WorkspaceSettingsOrganizationMemberQuery.generated';
import { useWorkspaceSettingsOrganizationMemberQuery } from './WorkspaceSettingsOrganizationMemberQuery.generated';
import type { WorkspaceSettingsOrganizationWorkspaceQuery } from './WorkspaceSettingsOrganizationWorkspaceQuery.generated';
import {
  useWorkspaceSettingsOrganizationWorkspaceQuery,
  WorkspaceSettingsOrganizationWorkspaceDocument,
} from './WorkspaceSettingsOrganizationWorkspaceQuery.generated';
import { WorkspaceSettingsSection } from './WorkspaceSettingsSection';
import type { FunctionComponent } from 'react';
import React, { useCallback } from 'react';
import { Button } from '@trello/nachos/button';
import styles from './WorkspaceSettingsOrganization.less';
import { getDomain } from '@trello/emails';
import { Analytics } from '@trello/atlassian-analytics';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_PII_STRING,
} from '@trello/privacy';
import { WorkspaceSettingsOrganizationTestIds } from '@trello/test-ids';

const format = forTemplate('org_account');

interface WorkspaceSettingsAddOrganizationProps {
  onAddOrganization(): void;
  workspace: WorkspaceSettingsOrganizationWorkspaceQuery['organization'];
  member: WorkspaceSettingsOrganizationMemberQuery['member'];
  isEditable: boolean;
  loading: boolean;
}
const WorkspaceSettingsAddOrganization: FunctionComponent<WorkspaceSettingsAddOrganizationProps> = ({
  workspace,
  member,
  onAddOrganization,
  isEditable,
  loading,
}) => {
  const organizationDisplayName = member?.domain?.displayName;
  return (
    <WorkspaceSettingsSection
      title={format('workspace-organization')}
      detail={
        <p>
          {format('workspace-domain-unset', {
            organizationName: organizationDisplayName,
            learnMore: (
              <a
                key="learn-more-link"
                href="https://support.atlassian.com/trello/docs/organizations-in-trello/"
                target="_blank"
              >
                {format('learn-more')}
              </a>
            ),
          })}
        </p>
      }
      isEditable={isEditable}
      action={
        <Button onClick={onAddOrganization} isDisabled={!isEditable || loading}>
          {format('move-workspace-to-org-button', {
            organizationName: organizationDisplayName,
          })}
        </Button>
      }
    />
  );
};

interface WorkspaceSettingsRemoveOrganizationProps {
  onRemoveOrganization(): Promise<void>;
  workspace: WorkspaceSettingsOrganizationWorkspaceQuery['organization'];
  isEditable: boolean;
  loading: boolean;
}
const WorkspaceSettingsRemoveOrganization: FunctionComponent<WorkspaceSettingsRemoveOrganizationProps> = ({
  workspace,
  isEditable,
  loading,
  onRemoveOrganization,
}) => {
  const {
    triggerRef,
    toggle,
    hide,
    popoverProps,
  } = usePopover<HTMLButtonElement>();
  const onClickRemove = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'removeWorkspaceDomainButton',
      source: 'teamSettingsScreen',
      containers: {
        organization: {
          id: workspace?.id,
        },
      },
    });
    toggle();
  }, [toggle, workspace?.id]);

  const onRemove = useCallback(async () => {
    await onRemoveOrganization();
    hide();
  }, [hide, onRemoveOrganization]);

  const organizationDisplayName =
    workspace?.domain?.displayName ?? workspace?.domainName;
  return (
    <>
      <WorkspaceSettingsSection
        title={format('workspace-organization')}
        detail={
          <p>
            {format('workspace-domain-set', {
              organizationName: organizationDisplayName,
              learnMore: (
                <a
                  key="learn-more-link"
                  href="https://support.atlassian.com/trello/docs/organizations-in-trello/"
                  target="_blank"
                >
                  {format('learn-more')}
                </a>
              ),
            })}
          </p>
        }
        isEditable={isEditable}
        action={
          <Button
            ref={triggerRef}
            onClick={onClickRemove}
            isDisabled={!isEditable || loading}
          >
            {format('remove-workspace-from-org-button', {
              organizationName: organizationDisplayName,
            })}
          </Button>
        }
      />
      <Popover title={format('remove-from-your-org')} {...popoverProps}>
        <PopoverContent noTopPadding noBottomPadding noHorizontalPadding>
          <div className={styles.warningPopover}>
            <div className={styles.warningContent}>
              {format('workspace-domain-removal-warning', {
                organizationName: organizationDisplayName,
              })}
            </div>

            <Button
              data-testid={
                WorkspaceSettingsOrganizationTestIds.RemoveWorkspaceFromOrgButton
              }
              appearance="danger"
              onClick={onRemove}
              isDisabled={!isEditable || loading}
            >
              {format('remove-workspace-from-org-button', {
                organizationName: organizationDisplayName,
              })}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

interface WorkspaceSettingsOrganizationProps {
  orgId: string;
  isEditable: boolean;
}

export const WorkspaceSettingsOrganization: FunctionComponent<WorkspaceSettingsOrganizationProps> = ({
  orgId,
  isEditable,
}) => {
  // Setup hooks
  const isFeatureEnabled = useFeatureFlag(
    'ruby.web.set-workspace-domain',
    false,
  );

  // Load data
  const {
    data: memberData,
    loading: loadingMember,
    error: memberError,
  } = useWorkspaceSettingsOrganizationMemberQuery({
    variables: {
      memberId: 'me',
    },
    skip: !isFeatureEnabled,
    waitOn: ['MemberHeader'],
  });
  const {
    data: workspaceData,
    loading: loadingWorkspace,
    error: workspaceError,
  } = useWorkspaceSettingsOrganizationWorkspaceQuery({
    variables: {
      orgId,
    },
    skip: !isFeatureEnabled,
    waitOn: ['MemberHeader'],
  });

  // Setup mutations
  const [
    updateOrganization,
    { loading: updateDomainLoading },
  ] = useWorkspaceSettingsOrganizationUpdateDomainMutation();
  const updateDomain = useCallback(
    async (domain: string | null) => {
      const traceId = Analytics.startTask({
        taskName: 'edit-organization/domain',
        source: 'workspaceSettingsDomainSectionScreen',
        attributes: {
          action: 'set',
        },
      });
      try {
        await updateOrganization({
          variables: {
            orgId,
            domain: domain ?? '',
            traceId,
          },
          refetchQueries: [
            {
              query: WorkspaceSettingsOrganizationWorkspaceDocument,
              variables: {
                orgId,
              },
              context: {
                operationName: 'WorkspaceSettingsOrganizationWorkspace',
                document: WorkspaceSettingsOrganizationWorkspaceDocument,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
      } catch (e) {
        // @ts-expect-error
        sendErrorEvent(e);
      }
    },
    [orgId, updateOrganization],
  );

  const workspace = workspaceData?.organization;
  const member = memberData?.member;
  const isWorkspaceDomainNameSet = Boolean(workspace?.domainName);
  const canWorkspaceDomainBeChanged = Boolean(workspace?.canEditDomain);

  const onAddOrganization = useCallback(async () => {
    const memberEmail = memberData?.member?.email ?? EMPTY_PII_STRING;
    Analytics.sendClickedButtonEvent({
      buttonName: 'addWorkspaceDomainButton',
      source: 'teamSettingsScreen',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });

    const traceId = Analytics.startTask({
      taskName: 'edit-organization/domain',
      source: 'workspaceSettingsDomainSectionScreen',
      attributes: {
        action: 'set',
      },
    });

    try {
      const domain = getDomain(memberEmail);
      if (!domain) {
        throw Error('Invalid domain from email');
      }

      await updateDomain(dangerouslyConvertPrivacyString(domain));

      Analytics.taskSucceeded({
        taskName: 'edit-organization/domain',
        traceId,
        source: 'workspaceSettingsDomainSectionScreen',
      });
    } catch (e) {
      // @ts-expect-error
      sendErrorEvent(e);
      Analytics.taskFailed({
        taskName: 'edit-organization/domain',
        traceId,
        source: 'workspaceSettingsDomainSectionScreen',
        // @ts-expect-error
        error: e,
      });
    }
  }, [memberData?.member?.email, orgId, updateDomain]);

  const onRemoveOrganization = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-organization/domain',
      source: 'workspaceSettingsDomainSectionScreen',
      attributes: {
        action: 'unset',
      },
    });

    try {
      await updateDomain('');
      Analytics.taskSucceeded({
        taskName: 'edit-organization/domain',
        traceId,
        source: 'workspaceSettingsDomainSectionScreen',
      });
    } catch (e) {
      // @ts-expect-error
      sendErrorEvent(e);
      Analytics.taskFailed({
        taskName: 'edit-organization/domain',
        traceId,
        source: 'workspaceSettingsDomainSectionScreen',
        // @ts-expect-error
        error: e,
      });
    }
  }, [updateDomain]);

  if (
    !isFeatureEnabled ||
    loadingMember ||
    loadingWorkspace ||
    memberError ||
    workspaceError ||
    !member?.domain ||
    !canWorkspaceDomainBeChanged
  ) {
    return null;
  }

  Analytics.sendScreenEvent({
    name: 'workspaceSettingsDomainSectionScreen',
    containers: {
      workspace: {
        id: orgId,
      },
    },
  });

  return isWorkspaceDomainNameSet ? (
    <WorkspaceSettingsRemoveOrganization
      workspace={workspace}
      onRemoveOrganization={onRemoveOrganization}
      isEditable={isEditable}
      loading={updateDomainLoading}
    />
  ) : (
    <WorkspaceSettingsAddOrganization
      member={member}
      workspace={workspace}
      onAddOrganization={onAddOrganization}
      isEditable={isEditable}
      loading={updateDomainLoading}
    />
  );
};
